import * as Yup from "yup";

import { defaultPropertyForm } from "../forms/DefaultFormData";
import { IPropertyFormDataType } from "../forms/FormDataTypes";
import { PropertyValidationSchema } from "../forms/validationSchemas/PropertyValidationSchema";
import { IListItemSimple } from "../zeos-api/contracts/ListUtility";

// Keep in sync with MaxBulkProperties in PropertyService.cs (zeos-api).
export const MAX_BULK_ROWS = 200;

const HVAC_PREFIX = "hvac:";
const PRIOR_WORK_PREFIX = "priorWork:";
const PRIOR_WORK_NONE_ID = 2;

const WIFI_VALUES = ["Yes", "No"] as const;

const FIXED_COLUMNS = [
  "companyKey",
  "propertyName",
  "address",
  "suite",
  "city",
  "state",
  "zip",
  "yearBuilt",
  "buildingType",
  "sqft",
  "floors",
  "units",
  "acreage",
  "roofSize",
  "land",
  "hasWifi",
  "paysBill",
  "bin",
  "bbl",
] as const;

const OPTIONAL_COLUMNS = [
  "electricUtilityProvider",
  "gasUtilityProvider",
] as const;

const NUMERIC_COLUMNS = ["sqft", "floors", "units", "acreage", "roofSize"] as const;

export interface IPropertyCsvLookups {
  hvacOptions: IListItemSimple[];
  priorWorkOptions: IListItemSimple[];
  buildingTypeOptions: IListItemSimple[];
}

export interface IBulkPropertyRow {
  line: number;
  companyKey: string;
  formData: IPropertyFormDataType;
  errors: string[];
}

/**
 * What the caller knows about the (optional) companies CSV, so property rows
 * can reference a company that's being created in the same bulk action.
 */
export interface ICompanyKeyContext {
  companyFileUploaded: boolean;
  validKeys: Set<string>;
}

export interface IValidatePropertyCsvResult {
  fileErrors: string[];
  rows: IBulkPropertyRow[];
}

/**
 * Quote-aware CSV parser. Handles quoted fields, escaped "" quotes, CRLF/LF,
 * and embedded newlines inside quoted fields. Strips a leading UTF-8 BOM.
 */
export const parseCsv = (text: string): string[][] => {
  const input = text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;
  const rows: string[][] = [];
  let row: string[] = [];
  let field = "";
  let inQuotes = false;
  let i = 0;

  const endField = () => {
    row.push(field);
    field = "";
  };
  const endRow = () => {
    endField();
    rows.push(row);
    row = [];
  };

  while (i < input.length) {
    const char = input[i];

    if (inQuotes) {
      if (char === '"') {
        if (input[i + 1] === '"') {
          field += '"';
          i += 2;
          continue;
        }
        inQuotes = false;
        i += 1;
        continue;
      }
      field += char;
      i += 1;
      continue;
    }

    if (char === '"') {
      inQuotes = true;
      i += 1;
    } else if (char === ",") {
      endField();
      i += 1;
    } else if (char === "\r") {
      i += 1;
    } else if (char === "\n") {
      endRow();
      i += 1;
    } else {
      field += char;
      i += 1;
    }
  }

  // Final field/row if the file doesn't end with a newline.
  if (field.length > 0 || row.length > 0) {
    endRow();
  }

  // Drop trailing blank lines.
  return rows.filter((r) => r.some((cell) => cell.trim().length > 0));
};

const csvCell = (value: string): string => {
  if (/[",\n\r]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
};

const csvLine = (cells: string[]): string => cells.map(csvCell).join(",");

const lookupColumnName = (prefix: string, option: IListItemSimple) =>
  `${prefix}${option.display}`;

/**
 * Builds a downloadable CSV template. Fixed columns first, then one 1/0
 * column per live HVAC and Prior Work reference option, so the template
 * always matches the current reference data.
 */
export const buildPropertyCsvTemplate = (lookups: IPropertyCsvLookups): string => {
  const hvacColumns = lookups.hvacOptions.map((o) => lookupColumnName(HVAC_PREFIX, o));
  const priorWorkColumns = lookups.priorWorkOptions.map((o) =>
    lookupColumnName(PRIOR_WORK_PREFIX, o)
  );
  const header = [
    ...FIXED_COLUMNS,
    ...OPTIONAL_COLUMNS,
    ...hvacColumns,
    ...priorWorkColumns,
  ];

  const exampleFixed: Record<(typeof FIXED_COLUMNS)[number], string> = {
    companyKey: "",
    propertyName: "Example Property",
    address: "123 Main St",
    suite: "",
    city: "Brooklyn",
    state: "NY",
    zip: "11201",
    yearBuilt: "1998",
    buildingType: lookups.buildingTypeOptions[0]?.display ?? "",
    sqft: "10000",
    floors: "4",
    units: "20",
    acreage: "",
    roofSize: "",
    land: "Owned",
    hasWifi: "Yes",
    paysBill: "Owner",
    bin: "",
    bbl: "",
  };

  const exampleHvac = lookups.hvacOptions.map((_, idx) => (idx === 0 ? "1" : "0"));
  // Prefer "None" as the example selection when it exists; otherwise fall back
  // to the first option, so the example row always passes the "min 1" rule.
  const examplePriorWorkId =
    lookups.priorWorkOptions.find((o) => o.dataKey === PRIOR_WORK_NONE_ID)
      ?.dataKey ?? lookups.priorWorkOptions[0]?.dataKey;
  const examplePriorWork = lookups.priorWorkOptions.map((o) =>
    o.dataKey === examplePriorWorkId ? "1" : "0"
  );

  const exampleRow = [
    ...FIXED_COLUMNS.map((col) => exampleFixed[col]),
    "National Grid",
    "National Grid",
    ...exampleHvac,
    ...examplePriorWork,
  ];

  const lines = [csvLine(header), csvLine(exampleRow)];
  return `﻿${lines.join("\r\n")}\r\n`;
};

const findLookupColumn = (
  headerLower: string[],
  prefix: string,
  option: IListItemSimple
): number => {
  const target = `${prefix}${option.display}`.toLowerCase().trim();
  return headerLower.findIndex((h) => h.trim() === target);
};

const isBlank = (value: string | undefined) => !value || value.trim().length === 0;

const parseFlag = (value: string | undefined): boolean => {
  const trimmed = (value ?? "").trim();
  return trimmed === "1" || trimmed.toLowerCase() === "true";
};

/**
 * Parses + validates a bulk-upload CSV against the same rules the single
 * property form uses (PropertyValidationSchema), plus a couple of CSV-specific
 * checks (numeric columns, unknown lookup names) that a hand-typed spreadsheet
 * is more likely to violate than the guided form ever could.
 */
export const validatePropertyCsv = (
  text: string,
  lookups: IPropertyCsvLookups,
  companyKeyContext?: ICompanyKeyContext
): IValidatePropertyCsvResult => {
  const table = parseCsv(text);
  if (table.length === 0) {
    return { fileErrors: ["The CSV file is empty."], rows: [] };
  }

  const header = table[0];
  const headerLower = header.map((h) => h.toLowerCase().trim());
  const dataRows = table.slice(1);

  const fileErrors: string[] = [];

  const requiredMissing = FIXED_COLUMNS.filter(
    (col) => !headerLower.includes(col.toLowerCase())
  );
  if (requiredMissing.length > 0) {
    fileErrors.push(`Missing required column(s): ${requiredMissing.join(", ")}`);
  }

  const hvacColumnIndexes = lookups.hvacOptions.map((o) => ({
    option: o,
    index: findLookupColumn(headerLower, HVAC_PREFIX, o),
  }));
  const missingHvacColumns = hvacColumnIndexes.filter((c) => c.index === -1);
  if (missingHvacColumns.length > 0) {
    fileErrors.push(
      `Missing HVAC column(s): ${missingHvacColumns
        .map((c) => lookupColumnName(HVAC_PREFIX, c.option))
        .join(", ")}`
    );
  }

  const priorWorkColumnIndexes = lookups.priorWorkOptions.map((o) => ({
    option: o,
    index: findLookupColumn(headerLower, PRIOR_WORK_PREFIX, o),
  }));
  const missingPriorWorkColumns = priorWorkColumnIndexes.filter((c) => c.index === -1);
  if (missingPriorWorkColumns.length > 0) {
    fileErrors.push(
      `Missing Prior Work column(s): ${missingPriorWorkColumns
        .map((c) => lookupColumnName(PRIOR_WORK_PREFIX, c.option))
        .join(", ")}`
    );
  }

  const knownColumns = new Set([
    ...FIXED_COLUMNS.map((c) => c.toLowerCase()),
    ...OPTIONAL_COLUMNS.map((c) => c.toLowerCase()),
    ...hvacColumnIndexes.map((c) => `${HVAC_PREFIX}${c.option.display}`.toLowerCase()),
    ...priorWorkColumnIndexes.map(
      (c) => `${PRIOR_WORK_PREFIX}${c.option.display}`.toLowerCase()
    ),
  ]);
  const unknownColumns = headerLower.filter((h) => h.length > 0 && !knownColumns.has(h));
  if (unknownColumns.length > 0) {
    fileErrors.push(`Unrecognized column(s): ${unknownColumns.join(", ")}`);
  }

  if (dataRows.length === 0) {
    fileErrors.push("The CSV file has no data rows.");
  }
  if (dataRows.length > MAX_BULK_ROWS) {
    fileErrors.push(
      `Too many rows (${dataRows.length}). Bulk upload supports at most ${MAX_BULK_ROWS} properties per file.`
    );
  }

  if (fileErrors.length > 0) {
    return { fileErrors, rows: [] };
  }

  const colIndex = (name: string) => headerLower.indexOf(name.toLowerCase());
  const cell = (rowValues: string[], name: string) => (rowValues[colIndex(name)] ?? "").trim();

  const buildingTypeByName = new Map(
    lookups.buildingTypeOptions.map((o) => [o.display.toLowerCase().trim(), o])
  );

  const rows: IBulkPropertyRow[] = dataRows.map((rowValues, rowIdx) => {
    const errors: string[] = [];
    const line = rowIdx + 2; // +1 for header, +1 for 1-based line numbers

    // Blank companyKey always means "the currently selected company" — no
    // lookup needed, and that's how every bulk upload worked before this
    // column existed. A filled-in key has to resolve against the companies
    // CSV uploaded alongside this one, if any.
    const companyKey = cell(rowValues, "companyKey");
    if (!isBlank(companyKey)) {
      if (!companyKeyContext?.companyFileUploaded) {
        errors.push(
          `companyKey "${companyKey}" was given, but no companies CSV was uploaded`
        );
      } else if (!companyKeyContext.validKeys.has(companyKey.toLowerCase())) {
        errors.push(
          `Unknown companyKey "${companyKey}" — it doesn't match any row in the companies CSV`
        );
      }
    }

    const buildingTypeName = cell(rowValues, "buildingType");
    const buildingType = isBlank(buildingTypeName)
      ? undefined
      : buildingTypeByName.get(buildingTypeName.toLowerCase());
    if (!isBlank(buildingTypeName) && !buildingType) {
      errors.push(`Unknown buildingType "${buildingTypeName}"`);
    }

    // land and paysBill are left to PropertyValidationSchema's own .oneOf()
    // checks below — duplicating them here would just double-report the same
    // error. hasWifi has no schema rule (it's commented out there, enforced
    // only by the form's radio buttons), so it needs a check here.
    const landValue = cell(rowValues, "land");
    const paysBillValue = cell(rowValues, "paysBill");

    const hasWifiValue = cell(rowValues, "hasWifi");
    if (
      !isBlank(hasWifiValue) &&
      !WIFI_VALUES.includes(hasWifiValue as (typeof WIFI_VALUES)[number])
    ) {
      errors.push(`hasWifi must be blank, "Yes", or "No" (got "${hasWifiValue}")`);
    }

    for (const numericCol of NUMERIC_COLUMNS) {
      const raw = cell(rowValues, numericCol);
      if (!isBlank(raw) && !/^\d+(\.\d+)?$/.test(raw)) {
        errors.push(`${numericCol} must be a plain number (got "${raw}")`);
      }
    }

    const hvacPrimaryTypeIds = hvacColumnIndexes
      .filter((c) => parseFlag(rowValues[c.index]))
      .map((c) => c.option.dataKey);

    const priorWorkTypeIds = priorWorkColumnIndexes
      .filter((c) => parseFlag(rowValues[c.index]))
      .map((c) => c.option.dataKey);

    if (
      priorWorkTypeIds.includes(PRIOR_WORK_NONE_ID) &&
      priorWorkTypeIds.length > 1
    ) {
      errors.push('priorWork:None cannot be combined with other prior work types');
    }

    const formData: IPropertyFormDataType = {
      ...defaultPropertyForm,
      propertyName: cell(rowValues, "propertyName"),
      address: cell(rowValues, "address"),
      suite: cell(rowValues, "suite"),
      city: cell(rowValues, "city"),
      state: cell(rowValues, "state"),
      zip: cell(rowValues, "zip"),
      yearBuilt: cell(rowValues, "yearBuilt"),
      buildingType: buildingType?.display ?? "",
      buildingTypeId: buildingType?.dataKey,
      espmPropertyTypeCategoryId: buildingType?.dataKey,
      sqft: cell(rowValues, "sqft"),
      floors: cell(rowValues, "floors"),
      units: cell(rowValues, "units"),
      acreage: cell(rowValues, "acreage"),
      roofSize: cell(rowValues, "roofSize"),
      land: (isBlank(landValue) ? undefined : landValue) as IPropertyFormDataType["land"],
      hasWifi: (isBlank(hasWifiValue) ? undefined : hasWifiValue) as IPropertyFormDataType["hasWifi"],
      paysBill: isBlank(paysBillValue) ? "" : paysBillValue,
      bin: cell(rowValues, "bin"),
      bbl: cell(rowValues, "bbl"),
      hvacPrimaryTypeIds,
      priorWorkTypeIds,
      electricUtilityProvider: cell(rowValues, "electricUtilityProvider"),
      gasUtilityProvider: cell(rowValues, "gasUtilityProvider"),
      file: [],
    };

    try {
      PropertyValidationSchema.validateSync(formData, { abortEarly: false });
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const zipPresent = !isBlank(formData.zip);
        const messages = err.inner.map((e) => e.message).filter((msg) => {
          if (
            zipPresent &&
            (msg === "Confirm the electric utility" ||
              msg === "Confirm the gas utility" ||
              msg === "Confirm the gas utility or choose No piped gas")
          ) {
            return false;
          }
          return true;
        });
        errors.push(...messages);
      } else {
        errors.push("Validation failed for this row.");
      }
    }

    return { line, companyKey, formData, errors };
  });

  return { fileErrors: [], rows };
};
